import React, {
    useCallback, useState, useContext, useMemo
} from 'react';
import { TranslatorContext } from '@jutro/locale';
import { useLocation } from 'react-router-dom';
import { logout } from 'saga-cep-specific-components';
import CommonConstants from 'sg-common-constants';
import _ from 'lodash';
import { Button, Link } from '@jutro/components';
import SgHelpDialog from './sg-help-dialog';

import messages from './SgCepHeader.messages';

const desktopMenuOptions = [
    {
        id: 'home',
        exact: true,
        path: '/home',
        showOnNavBar: true,
        title: {
            id: 'cep.components.sgCepHeader.navigation.PolicySummary'
        }
    },
    {
        id: 'changePolicy',
        exact: true,
        path: '/endorsement',
        showOnNavBar: true,
        title: {
            id: 'cep.components.sgCepHeader.navigation.AmendPolicy'
        }
    },
    {
        id: 'payments',
        exact: true,
        path: '/billing-summary',
        showOnNavBar: true,
        title: {
            id: 'cep.components.sgCepHeader.navigation.Payments'
        }
    },
    {
        id: 'documents',
        exact: true,
        path: '/documents',
        showOnNavBar: true,
        title: {
            id: 'cep.components.sgCepHeader.navigation.Documents'
        }
    },
    {
        id: 'makeClaim',
        exact: true,
        path: '/claim',
        showOnNavBar: true,
        title: {
            id: 'cep.components.sgCepHeader.navigation.Claims'
        }
    },
    {
        id: 'account',
        exact: true,
        path: '/ampuserprofile',
        showOnNavBar: true,
        title: {
            id: 'cep.components.sgCepHeader.navigation.Account'
        }
    }
];

const mobileMenuOptions = [
    {
        to: '/home',
        id: 'home',
        title: 'cep.components.sgCepHeader.navigation.PolicySummary'
    },
    {
        to: '/endorsement',
        id: 'changePolicy',
        title: 'cep.components.sgCepHeader.navigation.AmendPolicy'
    },
    {
        to: '/billing-summary',
        id: 'payments',
        title: 'cep.components.sgCepHeader.navigation.Payments'
    },
    {
        to: '/documents',
        id: 'documents',
        title: 'cep.components.sgCepHeader.navigation.Documents'
    },
    {
        to: '/claim',
        id: 'makeClaim',
        title: 'cep.components.sgCepHeader.navigation.Claims'
    },
    {
        to: '/faq',
        id: 'faq',
        title: 'cep.components.sgCepHeader.navigation.Faq'
    },
    {
        to: '/ampuserprofile',
        id: 'account',
        title: 'cep.components.sgCepHeader.navigation.Account'
    },
    {
        path: '/',
        id: 'logout',
        title: 'cep.components.sgCepHeader.navigation.Logout',
        action: () => {
            logout();
        }
    },
    {
        id: 'mysaga',
        path: 'https://www.saga.co.uk/my/account',
        title: 'cep.components.sgCepHeader.navigation.MySaga'
    },
];

function SgCepHeader() {
    const [showHelpDialog, setShowHelpDialog] = useState(false);
    const [isMenuOpened, setIsMenuOpened] = useState(false);

    const location = useLocation();

    const translator = useContext(TranslatorContext);

    const activeOption = useMemo(() => {
        if (desktopMenuOptions.find((option) => location.pathname.includes(option.path))) {
            return translator(desktopMenuOptions.find((option) => location.pathname.includes(option.path)).title);
        }
        return '';
    }, [location.pathname, translator]);

    const closeDialog = useCallback(() => {
        setShowHelpDialog(false);
    }, [showHelpDialog]);

    const handleOpenCloseHelpDialog = useCallback(() => {
        window.scrollTo(0, 0);
        setShowHelpDialog(!showHelpDialog);
    }, [showHelpDialog]);

    const handleOpenCloseMobileMenu = useCallback(() => {
        if (isMenuOpened) {
            document.getElementById('cepContent').style.display = 'block';
        } else {
            document.getElementById('cepContent').style.display = 'none';
        }
        setIsMenuOpened(!isMenuOpened);
    }, [isMenuOpened]);

    const handleMobileMenuItemAction = useCallback((option, event) => {
        handleOpenCloseMobileMenu();
        if (option.action) {
            event.preventDefault();
            option.action();
        }
    }, [isMenuOpened]);

    const drawMobileMenuOptions = () => {
        return mobileMenuOptions.map((option) => {
            return (
                <li key={option.title}>
                    { option.to
                        ? (
                            <Link
                                id={`mobileMenuLink${option.id}`}
                                to={option.to}
                                onClick={handleMobileMenuItemAction}
                                role="menuitem"
                                className="sgMenuOptionMobile"
                            >
                                {translator(_.find(messages, { id: option.title })) }
                            </Link>
                        )
                        : (
                            <Link
                                id={`mobileMenuLink${option.id}`}
                                href={option.path}
                                onClick={(e) => handleMobileMenuItemAction(option, e)}
                                role="menuitem"
                                className="sgMenuOptionMobile"
                            >
                                {translator(_.find(messages, { id: option.title })) }
                            </Link>
                        )
                    }
                </li>
            );
        });
    };

    // Only shows in the header for "intermediate" screen sizes
    const drawDesktopOptions = useMemo(() => {
        const isActive = (option) => {
            return location.pathname.includes(option.path);
        };
        return desktopMenuOptions.map((option) => {
            return (
                <div className="sg-menu-tab" key={option.id}>
                    <Link to={option.path} id={`menuLink${option.id}`} className={`sgPrimaryLightButton ${isActive(option) && 'active'}`}>
                        {translator(option.title)}
                    </Link>
                </div>
            );
        });
    }, [location, translator]);

    return (
        <div className="headerContent">
            <SgHelpDialog showDialog={showHelpDialog} closeDialog={closeDialog} />
            <div className="headerSticky" id="sgStickyHeader">
                <div className="container">
                    <div className="logo">
                        <h1>
                            <Link to="/home">
                                <img
                                    className="logoImage"
                                    alt="Saga Logo"
                                    src="https://asset.saga.co.uk/common/brand2021/logo/Saga_Logo_Indigo.svg"
                                />
                            </Link>
                        </h1>
                    </div>
                    <Button
                        id="helpButton"
                        onClick={handleOpenCloseHelpDialog}
                        className={`helpButton ${isMenuOpened ? 'sg-hide' : 'sg-show'}`}
                    >
                        <i className="sgFAQ" aria-hidden="true" />
                        {translator(messages.Help)}
                    </Button>
                </div>
            </div>
            <div className="menuContainer">
                <div className="menuDesktop">
                    <div className="container">
                        <div className="menu" role="menubar" aria-label="Main menu">
                            {drawDesktopOptions}
                        </div>
                    </div>
                </div>

                <div className="mobileMenu">
                    <div role="menubar" aria-label="Main menu">
                        <div className="mobileMenuButtonContainer">
                            <Button className="mobileMenuButton" onClick={handleOpenCloseMobileMenu}>
                                <i className={`gw-icon-caret mir mi-expand-more ${isMenuOpened ? 'sg-collapse' : ''}`} />
                                <span>{ !isMenuOpened ? 'Menu' : 'Close menu'}</span>
                            </Button>
                        </div>
                        <ul className={`mobileMenuOptions ${isMenuOpened ? 'visible' : ''}`}>
                            {drawMobileMenuOptions()}
                        </ul>
                    </div>
                </div>
            </div>
            <div className="headerBreadcrumbs">
                <Link id="mySagaLink" href={CommonConstants.SAGA_LINKS.MYSAGA_URL} target="_blank">{translator(messages.YourInsurance)}</Link>
                <i className="gw-icon-caret mir mi-chevron-right" />
                <span>{activeOption}</span>
            </div>
        </div>
    );
}

export default SgCepHeader;
